import { useFormikContext } from 'formik';

import { ensureDefined } from '@e-socna/utils';
import { getDocumentName } from '@e-socna/data-layer';
import type { FolderWorkflowId } from '@e-socna/data-layer';

import { FolderPage_OneFolder_QueryDocument } from '../../../pages/Folder/hooks/useFindOneFolderQuery.hook';
import { MissionPage_OneMission_QueryDocument } from '../../../pages/Mission/hooks/useFindOneMissionQuery.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import { FolderBusinessEntityActorsFormStep } from '../../FolderBusinessEntity/steps/Actors';
import { useActorListsChangeFreezes } from '../../FolderBusinessEntity/steps/Actors/hooks/useActorListsChangeFreezes.hook';
import type { Props } from '../../FolderBusinessEntity/steps/Actors/types/props.type';

import { useCreateFolderActorMutation } from '../hooks/useCreateFolderActorMutation.hook';
import { useRemoveFolderActorMutation } from '../hooks/useRemoveFolderActorMutation.hook';
import { useUpdateFolderActorMutation } from '../hooks/useUpdateFolderActorMutation.hook';
import { useFolderCreationRefusal } from '../hooks/useFolderCreationRefusal.hook';
import type { FolderForm_Folder } from '../fragments/folder.fragment';

export const FolderActorsFormStep = ({
  fallbackActors,
  id,
  isInternal,
  paths,
  useDirectMutation,
}: Omit<
  Props,
  'isUnderConfidentiality' | 'mutations' | 'queryKeysToInvalidate' | 'rules'
>) => {
  const { values } = useFormikContext<FolderForm_Folder>();
  // The creation takes the roles held through the internal actors: their step tells a refusal.
  const creationRefusal = useFolderCreationRefusal(
    isInternal && !useDirectMutation,
  );
  const previewFreezes = useActorListsChangeFreezes('folder', values._id);

  const { mutate: createActorMutation } = useCreateFolderActorMutation();
  const { mutate: removeActorMutation } = useRemoveFolderActorMutation();
  const { mutate: updateActorMutation } = useUpdateFolderActorMutation();

  const mutations = {
    create: createActorMutation,
    remove: removeActorMutation,
    update: updateActorMutation,
  };

  const { folderWorkflowsIdMap } = useReferencesContext();
  const { workflow } = ensureDefined({
    workflow: folderWorkflowsIdMap[values.workflowId as FolderWorkflowId],
  });

  return (
    <FolderBusinessEntityActorsFormStep
      creationRefusal={creationRefusal}
      fallbackActors={fallbackActors}
      id={id}
      isInternal={isInternal}
      isUnderConfidentiality={values.isConfidential}
      mutations={mutations}
      paths={paths}
      previewFreezes={useDirectMutation ? previewFreezes : undefined}
      queryKeysToInvalidate={
        [
          [
            getDocumentName(FolderPage_OneFolder_QueryDocument),
            { id: values._id },
          ],
          // The missions inheriting the lists of the folder.
          [getDocumentName(MissionPage_OneMission_QueryDocument)],
        ] as readonly (readonly string[])[]
      }
      rules={workflow.settings.actorableRules}
      useDirectMutation={useDirectMutation}
    />
  );
};
